import type { ReactNode } from "react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useNavigate, useRouter } from "@tanstack/react-router"
import OrderedList from "@tiptap/extension-ordered-list"
import { formatDistanceToNow } from "date-fns"
import { AnimatePresence, motion } from "motion/react"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconChevronUpOutline18 as ChevronUp,
	IconLanguageOutline18 as Languages,
} from "nucleo-ui-outline-18"

import type { HunkRange } from "@productlane/lib/src/prosemirror-diff"
import type { HelpCenterArticle, HelpCenterGroup } from "@productlane/zero"
import { DiffRenderExtension } from "@productlane/lib"
import { isArticleCoverHidden } from "@productlane/lib/src/article-cover"
import { cn } from "@productlane/lib/src/browser"
import { articleIconOrDefault } from "@productlane/lib/src/nucleo-icon-search"
import {
	computeReviewMerge,
	computeSideBySideRows,
} from "@productlane/lib/src/prosemirror-diff"
import { ShiftEnterParagraph } from "@productlane/lib/src/tiptap/extensions"
import {
	Avatar,
	Badge,
	Button,
	GitHubIcon as Github,
	LucideIcon,
	Sheet,
	SheetContent,
	Shortcut,
	Textarea,
	useBreakpoints,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { DraftState } from "./review-language"
import type { ReviewViewMode } from "./review-view-mode"
import type {
	SuggestionProposal,
	SuggestionRow,
} from "./use-article-suggestions"
import type { Editor } from "@tiptap/react"
import { DefaultArticleCover } from "@/components/default-article-cover"
import { DefaultEditor } from "@/components/editor/default-editor"
import { useArticleLinkPills } from "@/components/editor/help-articles/use-article-link-pills"
import { NucleoFillIconPicker } from "@/components/nucleo-fill-icon-picker"
import { toast } from "@/components/toast"
import { useUndoContext } from "@/context/undo-context"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { scrollParent } from "@/lib/scroll-parent"
import { useRole } from "@/lib/useRole"
import { mutateOk, useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	createDraftKey,
	useReviewDraftStore,
} from "@/stores/review-draft-store"
import { LanguageSelector } from "../../settings/settings-pages/language-selector"
import { isApiProposal } from "./aggregate-proposals"
import { DocsHeaderTabs } from "./docs-header-tabs"
import { useHelpCenterLanguage } from "./help-center-context"
import { HiddenArticleBanner } from "./hidden-article-banner"
import { ProposalFolder } from "./proposal-folder"
import { iconForKind, iconForProposal } from "./proposal-icon"
import { ProposalSources } from "./proposal-sources"
import { ReviewBulkActions } from "./review-bulk-actions"
import { ReviewDiff } from "./review-diff"
import {
	FALLBACK_PROPOSAL_LANGUAGE,
	findTranslation,
	resolveDraftState,
	resolveReviewDocument,
	resolveReviewLanguage,
	reviewLanguageMessage,
} from "./review-language"
import { nextSuggestionRow, reviewRouteForRow } from "./review-navigation"
import { applyProposedEdits } from "./review-split-rows"
import { useReviewViewMode, ViewModeToggle } from "./review-view-mode"
import { scrollWithinContainer } from "./scroll-within"
import { toMergeInput, useArticleSuggestions } from "./use-article-suggestions"

type ArticleWithTranslations = HelpCenterArticle & {
	translations?: readonly {
		language: string
		title?: string | null
		content?: string | null
	}[]
}

interface ExistingArticleProps {
	mode: "existing"
	row: SuggestionRow
	article: ArticleWithTranslations
	group?: HelpCenterGroup
}

interface CreateProposalProps {
	mode: "create"
	proposal: SuggestionProposal
}

type Props = ExistingArticleProps | CreateProposalProps

export const HelpCenterArticleReviewView = memo((props: Props) => {
	if (props.mode === "create") {
		return <CreateReviewView proposal={props.proposal} />
	}
	return (
		<ExistingReviewView
			row={props.row}
			article={props.article}
			group={props.group}
		/>
	)
})
HelpCenterArticleReviewView.displayName = "HelpCenterArticleReviewView"

// Delay between closing the mobile sheet and scrolling the editor to a diff.
// Matches the sheet's exit animation (data-[state=closed]:duration-300) so the
// scroll happens just as the article surface comes back into focus.
const SHEET_CLOSE_BEFORE_SCROLL_MS = 300

const EMPTY_DISCARDED: ReadonlySet<string> = new Set()

function ExistingReviewView({
	row,
	article,
}: {
	row: SuggestionRow
	article: ArticleWithTranslations
	group?: HelpCenterGroup
}) {
	const { isMobile } = useBreakpoints()
	const { role } = useRole()
	const canEdit = role !== "VIEWER"
	const draftKey = article.id
	const [, setProposedEdits] = useState<
		Map<string, { html: string; rowIndices: number[] }>
	>(() => new Map())
	const [, setDraftEdits] = useState<
		Map<string, { html: string; rowIndices: number[] }>
	>(() => new Map())
	const setDraft = useReviewDraftStore((state) => state.setDraft)
	const [sheetOpen, setSheetOpen] = useState(false)
	const [viewMode, setViewMode] = useReviewViewMode()
	// Two columns of article body have nowhere to go on a phone.
	const effectiveViewMode: ReviewViewMode = isMobile ? "flat" : viewMode

	const articleHtml = typeof article.content === "string" ? article.content : ""

	// The change is drafted in every enabled language when the proposal opens,
	// so a reviewer reads it the way their customers will.
	const { selectedLanguage } = useHelpCenterLanguage()
	const workspaceForLanguages = useZeroWorkspace()
	const translations = article.translations ?? []
	const proposalLanguage =
		workspaceForLanguages?.defaultLanguage ?? FALLBACK_PROPOSAL_LANGUAGE
	const languageState = resolveReviewLanguage({
		selectedLanguage: selectedLanguage.code,
		proposalLanguage,
		enabledLanguages: workspaceForLanguages?.enabledLanguages ?? [
			proposalLanguage,
		],
		translations,
	})
	const selectedTranslation = findTranslation(
		translations,
		selectedLanguage.code,
	)

	const soleProposalId = row.proposals.length === 1 ? row.proposals[0]!.id : ""
	const [proposalDrafts] = useQuery(
		queries.proposalTranslations(soleProposalId),
		{ enabled: !!soleProposalId && !languageState.isProposalLanguage },
	)
	const draftState = resolveDraftState({
		selectedLanguage: selectedLanguage.code,
		proposalLanguage,
		proposalCount: row.proposals.length,
		drafts: proposalDrafts ?? [],
	})
	const draftContent = draftState.kind === "ready" ? draftState.content : ""
	const draftTitle = draftState.kind === "ready" ? draftState.title : null

	const zeroForDrafts = useZero()
	const saveDraftTranslation = (
		patch: { title?: string } | { content: string },
	) => {
		if (!soleProposalId) return
		void mutateOk(
			zeroForDrafts.mutate(
				mutators.helpCenterArticleProposals.updateTranslation({
					proposalId: soleProposalId,
					language: selectedLanguage.code,
					...patch,
				}),
			),
		)
	}

	// Hunks the reviewer has discarded (`${proposalId}#${index}`). The merge
	// preview, the editor decorations and the accept call all recompute from an
	// "effective" step list that drops these; the stored proposal row is never
	// touched.
	const [discardedHunkIds, setDiscardedHunkIds] = useState<ReadonlySet<string>>(
		() => new Set(),
	)

	// Merge all OPEN proposals for this article into one preview HTML.
	// `useArticleSuggestions` already sorts `row.proposals` deterministically,
	// so the merge is stable across remounts. DELETE proposals contribute
	// empty steps and pass through naturally — the rail's "Accept" on the
	// delete card is what actually removes the file.
	const mergeInputs = useMemo(
		() => row.proposals.map(toMergeInput),
		[row.proposals],
	)
	// One discard-aware merge pass yields the preview HTML, per-proposal outcomes,
	// the per-hunk diff ranges (each carrying data-proposal-id + data-hunk-id), and
	// the hunk counts. `hunkRanges` is null when a guardrail trips on a very large
	// article, in which case the editor falls back to the legacy base-vs-merged
	// diff so content still renders. Discarded hunks carry no range, so they
	// vanish from the preview.
	const review = useMemo(
		() => computeReviewMerge(articleHtml, mergeInputs, discardedHunkIds),
		[articleHtml, mergeInputs, discardedHunkIds],
	)
	const merged = review
	const hunkRanges = review.hunkRanges
	const hunkCountByProposal = review.hunkCountByProposal
	// Skip even the fallback base diff on huge articles so the editor never pays
	// for a full-document diff on mount.
	const diffBaseHtml =
		hunkRanges === null && articleHtml.length > 400_000 ? "" : articleHtml
	const reviewDocument = resolveReviewDocument({
		isProposalLanguage: languageState.isProposalLanguage,
		draft: draftState,
		translationContent: selectedTranslation?.content,
		proposalBaseHtml: diffBaseHtml,
		proposalMergedHtml: merged.resultHtml,
	})
	const showsProposalDiff =
		languageState.isProposalLanguage || !reviewDocument.isSelectedLanguage
	const discardedByProposal = useMemo(() => {
		const m = new Map<string, string[]>()
		for (const id of discardedHunkIds) {
			const proposalId = id.split("#")[0] ?? id
			const arr = m.get(proposalId)
			if (arr) arr.push(id)
			else m.set(proposalId, [id])
		}
		return m
	}, [discardedHunkIds])

	const onDiscardHunk = useCallback((hunkId: string) => {
		setDiscardedHunkIds((prev) => {
			const next = new Set(prev)
			if (next.has(hunkId)) next.delete(hunkId)
			else next.add(hunkId)
			return next
		})
	}, [])
	const onRestoreProposal = useCallback((proposalId: string) => {
		setDiscardedHunkIds((prev) => {
			const next = new Set(
				[...prev].filter((id) => id.split("#")[0] !== proposalId),
			)
			return next
		})
	}, [])

	const [hoveredProposalId, setHoveredProposalId] = useState<string | null>(
		null,
	)

	// Bidirectional highlight: when either a diff or its rail card is hovered,
	// flip `data-proposal-active` on every matching diff decoration in the
	// editor so the strong-state CSS rule (injected by DiffRenderExtension)
	// kicks in. Diffs all carry both `data-proposal-id` and `data-proposal-diff`,
	// so the combined selector keeps card data attrs (which only have
	// `data-proposal-id`) out of this loop — those are highlighted via the
	// React-controlled `isHovered` className on the card itself.
	useEffect(() => {
		const all = document.querySelectorAll<HTMLElement>(
			"[data-proposal-id][data-proposal-diff]",
		)
		for (const el of all) {
			const matches =
				hoveredProposalId != null &&
				el.getAttribute("data-proposal-id") === hoveredProposalId
			if (matches) el.setAttribute("data-proposal-active", "true")
			else el.removeAttribute("data-proposal-active")
		}
	}, [hoveredProposalId])

	// Mobile: tapping a diff highlight in the editor opens the sheet and
	// scrolls the matching card into view. Mirrors the existing card → editor
	// scroll wiring inside `ProposalCard`. Delegated listener keeps the change
	// scoped to this file rather than threading a click callback through the
	// editor extension.
	useEffect(() => {
		if (!isMobile) return
		const onClick = (event: MouseEvent) => {
			const target = event.target as Element | null
			if (!target || typeof target.closest !== "function") return
			if (target.closest("[data-hunk-control]")) return
			const diffEl = target.closest<HTMLElement>(
				"[data-proposal-id][data-proposal-diff]",
			)
			if (!diffEl) return
			const id = diffEl.getAttribute("data-proposal-id")
			if (!id) return
			setSheetOpen(true)
			// Wait for the sheet to mount + animate in before scrolling the
			// card into view so the sheet's body has layout to scroll.
			setTimeout(() => {
				const card = document.querySelector<HTMLElement>(
					`[data-proposal-card][data-proposal-id="${id}"]`,
				)
				const cardScroller = card ? scrollParent(card) : null
				if (card && cardScroller) scrollWithinContainer(cardScroller, card)
				setHoveredProposalId(id)
			}, 320)
		}
		document.addEventListener("click", onClick)
		return () => document.removeEventListener("click", onClick)
	}, [isMobile])

	const proposedTitle = useMemo(() => {
		const editTitle = row.proposals.find(
			(p) => p.kind === "EDIT" && p.proposedTitle,
		)?.proposedTitle
		return editTitle ?? article.title
	}, [row.proposals, article.title])

	const outcomeById = useMemo(
		() => new Map(merged.outcomes.map((o) => [o.id, o])),
		[merged.outcomes],
	)

	const handleMobileCardScroll = (proposalId: string) => {
		setSheetOpen(false)
		setTimeout(() => {
			const editorDiffs = document.querySelectorAll<HTMLElement>(
				"[data-proposal-id][data-proposal-diff]",
			)
			for (const el of editorDiffs) {
				if (el.getAttribute("data-proposal-id") === proposalId) {
					const scroller = scrollParent(el)
					if (scroller) scrollWithinContainer(scroller, el)
					return
				}
			}
		}, SHEET_CLOSE_BEFORE_SCROLL_MS)
	}

	return (
		<div className="flex h-full flex-col">
			<ReviewHeader
				viewMode={effectiveViewMode}
				onViewModeChange={setViewMode}
				actions={
					<ReviewBulkActions
						proposals={row.proposals}
						articleId={article.id}
						discardedByProposal={discardedByProposal}
						hunkCountByProposal={hunkCountByProposal}
					/>
				}
			/>
			{article.visibility !== "PUBLIC" && (
				<HiddenArticleBanner visibility={article.visibility} />
			)}
			<ReviewLanguageBanner
				languageName={selectedLanguage.name}
				state={languageState}
				draft={draftState}
			/>
			<div
				className={cn("flex min-h-0 flex-1", isMobile && "relative flex-col")}
			>
				<div
					data-sheet-open={isMobile && sheetOpen ? "true" : undefined}
					className={cn(
						"flex min-h-0 flex-1 flex-col",
						isMobile &&
							"duration-[380ms] ease-[cubic-bezier(.23,1,.32,1)] origin-top transition-transform data-[sheet-open=true]:scale-[0.96] data-[sheet-open=true]:overflow-hidden data-[sheet-open=true]:rounded-2xl",
					)}
				>
					<ArticleShell
						contentKey={selectedLanguage.code}
						icon={article.icon}
						title={
							showsProposalDiff
								? proposedTitle
								: (draftTitle ?? selectedTranslation?.title ?? article.title)
						}
						canEdit={
							canEdit &&
							(languageState.isProposalLanguage || draftState.kind === "ready")
						}
						onProposedEdit={(key, edit) => {
							if (draftState.kind === "ready") {
								setDraftEdits((prev) => {
									const next = new Map(prev)
									next.set(key, edit)
									saveDraftTranslation({
										content: applyProposedEdits(
											computeSideBySideRows("", draftContent),
											next,
										),
									})
									return next
								})
								return
							}
							setProposedEdits((prev) => {
								const next = new Map(prev)
								next.set(key, edit)
								setDraft(draftKey, {
									html: applyProposedEdits(
										computeSideBySideRows(
											diffBaseHtml,
											merged.resultHtml,
											hunkRanges ?? undefined,
										),
										next,
									),
								})
								return next
							})
						}}
						onTitleChange={
							languageState.isProposalLanguage
								? (title) => setDraft(draftKey, { title })
								: draftState.kind === "ready"
									? (title) => saveDraftTranslation({ title })
									: undefined
						}
						onContentChange={
							languageState.isProposalLanguage
								? (html) => setDraft(draftKey, { html })
								: draftState.kind === "ready"
									? (html) => saveDraftTranslation({ content: html })
									: undefined
						}
						summary={article.summary ?? ""}
						imageUrl={article.imageUrl ?? null}
						contentHtml={reviewDocument.mergedHtml}
						diffBaseHtml={reviewDocument.baseHtml}
						hunkRanges={
							showsProposalDiff ? (hunkRanges ?? undefined) : undefined
						}
						discardedHunkIds={discardedHunkIds}
						onDiscardHunk={
							languageState.isProposalLanguage && hunkRanges !== null
								? onDiscardHunk
								: undefined
						}
						viewMode={effectiveViewMode}
						foldUnchanged={showsProposalDiff}
						baseTitle={
							showsProposalDiff
								? article.title
								: (selectedTranslation?.title ?? article.title)
						}
						hoveredProposalId={hoveredProposalId}
						onProposalHover={setHoveredProposalId}
					/>
				</div>
				{isMobile ? (
					<>
						<MobileProposalsPill
							proposals={row.proposals}
							hidden={sheetOpen}
							onClick={() => setSheetOpen(true)}
						/>
						<MobileProposalsSheet
							open={sheetOpen}
							onOpenChange={setSheetOpen}
							proposals={row.proposals}
							outcomeById={outcomeById}
							articleId={article.id}
							hoveredProposalId={hoveredProposalId}
							onHoverProposal={setHoveredProposalId}
							onCardScroll={handleMobileCardScroll}
							discardedByProposal={discardedByProposal}
							hunkCountByProposal={hunkCountByProposal}
							onRestoreProposal={onRestoreProposal}
						/>
					</>
				) : (
					<ReviewRail
						proposals={row.proposals}
						outcomeById={outcomeById}
						articleId={article.id}
						hoveredProposalId={hoveredProposalId}
						onHoverProposal={setHoveredProposalId}
						discardedByProposal={discardedByProposal}
						hunkCountByProposal={hunkCountByProposal}
						onRestoreProposal={onRestoreProposal}
					/>
				)}
			</div>
		</div>
	)
}

function CreateReviewView({ proposal }: { proposal: SuggestionProposal }) {
	const { isMobile } = useBreakpoints()
	const { role } = useRole()
	const canEdit = role !== "VIEWER"
	const [sheetOpen, setSheetOpen] = useState(false)

	const merged = useMemo(
		() => computeReviewMerge("", [toMergeInput(proposal)], EMPTY_DISCARDED),
		[proposal],
	)

	// Same key the accept paths read, so an edit made here lands in the article.
	const draftKey = createDraftKey(proposal.id)
	const draft = useReviewDraftStore((state) => state.drafts[draftKey])
	const setDraft = useReviewDraftStore((state) => state.setDraft)

	const proposals = useMemo(() => [proposal], [proposal])
	const outcomeById = useMemo(
		() =>
			new Map([
				[proposal.id, { id: proposal.id, applied: true, conflicts: [] }],
			]),
		[proposal.id],
	)
	const noopHoverProposal = () => {
		/* CREATE has only one card; nothing to highlight bi-directionally */
	}
	const handleMobileCardScroll = () => {
		setSheetOpen(false)
	}

	return (
		<div className="flex h-full flex-col">
			<ReviewHeader
				actions={<ReviewBulkActions proposals={proposals} articleId={null} />}
			/>
			<div
				className={cn("flex min-h-0 flex-1", isMobile && "relative flex-col")}
			>
				<div
					data-sheet-open={isMobile && sheetOpen ? "true" : undefined}
					className={cn(
						"flex min-h-0 flex-1 flex-col",
						isMobile &&
							"duration-[380ms] ease-[cubic-bezier(.23,1,.32,1)] origin-top transition-transform data-[sheet-open=true]:scale-[0.96] data-[sheet-open=true]:overflow-hidden data-[sheet-open=true]:rounded-2xl",
					)}
				>
					<NewArticleEditor
						key={proposal.id}
						proposal={proposal}
						icon={articleIconOrDefault(proposal.proposedIcon)}
						title={draft?.title ?? proposal.proposedTitle ?? ""}
						initialHtml={draft?.html ?? merged.resultHtml}
						canEdit={canEdit}
						onTitleChange={(title) => setDraft(draftKey, { title })}
						onContentChange={(html) => setDraft(draftKey, { html })}
					/>
				</div>
				{isMobile ? (
					<>
						<MobileProposalsPill
							proposals={proposals}
							hidden={sheetOpen}
							onClick={() => setSheetOpen(true)}
						/>
						<MobileProposalsSheet
							open={sheetOpen}
							onOpenChange={setSheetOpen}
							proposals={proposals}
							outcomeById={outcomeById}
							articleId={null}
							hoveredProposalId={null}
							onHoverProposal={noopHoverProposal}
							onCardScroll={handleMobileCardScroll}
						/>
					</>
				) : (
					<ReviewRail
						proposals={proposals}
						outcomeById={outcomeById}
						articleId={null}
						hoveredProposalId={null}
						onHoverProposal={noopHoverProposal}
					/>
				)}
			</div>
		</div>
	)
}

// Mirrors the chrome of `HelpCenterHeader`: same height, border and blur, so
// switching between "view" and "review" doesn't shift the page header geometry.
// Only review controls ride along; article and docs-site actions stay out.
function ReviewHeader({
	viewMode,
	onViewModeChange,
	actions,
}: {
	viewMode?: ReviewViewMode
	onViewModeChange?: (mode: ReviewViewMode) => void
	actions?: ReactNode
}) {
	const workspace = useZeroWorkspace()
	const { selectedLanguage, setSelectedLanguage } = useHelpCenterLanguage()
	const enabledLanguages = workspace?.enabledLanguages ?? [
		workspace?.defaultLanguage ?? FALLBACK_PROPOSAL_LANGUAGE,
	]
	return (
		<div className="flex max-h-12 min-h-12 w-full flex-shrink-0 items-center justify-end gap-2 border-b border-gray-200 py-2 pl-[17.5px] pr-3 backdrop-blur-xl dark:border-gray-700/80 dark:bg-gray-900/80">
			<DocsHeaderTabs />
			{enabledLanguages.length > 1 && (
				<LanguageSelector
					languages={enabledLanguages}
					selectedLanguage={selectedLanguage}
					onLanguageSelect={setSelectedLanguage}
					variant="code-only"
				/>
			)}
			{viewMode && onViewModeChange && (
				<ViewModeToggle value={viewMode} onChange={onViewModeChange} />
			)}
			{actions}
		</div>
	)
}

function ReviewLanguageBanner({
	languageName,
	state,
	draft,
}: {
	languageName: string
	state: ReturnType<typeof resolveReviewLanguage>
	draft: DraftState
}) {
	const message = reviewLanguageMessage({ languageName, state, draft })
	if (!message) return null
	return (
		<div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs text-secondary dark:border-gray-700/80 dark:bg-gray-850">
			<Languages className="h-3.5 w-3.5 flex-none" />
			<span>{message}</span>
		</div>
	)
}

// Mirrors the body layout of help-center-article.tsx (group breadcrumb, title,
// summary, cover image, editor) so the review view feels identical to the live
// article — read-only, with the diff overlay applied to the editor.
function ArticleShell({
	contentKey,
	icon,
	title,
	summary,
	imageUrl,
	contentHtml,
	diffBaseHtml,
	hunkRanges,
	discardedHunkIds,
	onDiscardHunk,
	onProposalHover,
	viewMode = "flat",
	baseTitle,
	hoveredProposalId,
	canEdit = false,
	onProposedEdit,
	onTitleChange,
	onContentChange,
	foldUnchanged,
}: {
	icon: string
	title: string
	summary: string
	imageUrl: string | null
	contentHtml: string
	diffBaseHtml: string
	hunkRanges?: HunkRange[]
	discardedHunkIds?: ReadonlySet<string>
	onDiscardHunk?: (hunkId: string) => void
	onProposalHover?: (id: string | null) => void
	viewMode?: ReviewViewMode
	/** Remounts the editor when the reviewed language changes. */
	contentKey?: string
	baseTitle?: string
	hoveredProposalId?: string | null
	canEdit?: boolean
	onProposedEdit?: (
		key: string,
		edit: { html: string; rowIndices: number[] },
	) => void
	onTitleChange?: (title: string) => void
	onContentChange?: (html: string) => void
	foldUnchanged?: boolean
}) {
	const workspace = useZeroWorkspace()
	const scrollRef = useRef<HTMLDivElement>(null)
	// Accepting or skipping a hunk rewrites contentHtml. Keyed on that, the
	// jump below re-fired on every decision and threw the reader back to the
	// first diff, which is what made the page feel like it was moving on its
	// own. One jump per article is the whole intent.
	const jumpedForRef = useRef<string | null>(null)

	// Diff decorations mount after the editor, so poll for the first one before
	// scrolling. Skip the jump when it's already near the top.
	useEffect(() => {
		const container = scrollRef.current
		if (!container) return
		if (jumpedForRef.current === diffBaseHtml) return
		let raf = 0
		let tries = 0
		const run = () => {
			const first = container.querySelector<HTMLElement>(
				"[data-proposal-diff],[data-diff-row]",
			)
			if (first) {
				jumpedForRef.current = diffBaseHtml
				scrollWithinContainer(container, first)
				return
			}
			if (tries++ < 60) raf = requestAnimationFrame(run)
		}
		raf = requestAnimationFrame(run)
		return () => cancelAnimationFrame(raf)
	}, [diffBaseHtml])

	// The block-paired diff is what a reviewer reads; the read-only editor is the
	// fallback for an article too large to pair.
	const legacyEditorView = (
		<div className="flex cursor-default flex-row gap-10 px-4 pt-6 sm:px-6 sm:pt-8 [&_.ProseMirror]:cursor-default">
			<div className="mx-auto flex min-w-0 max-w-[652px] flex-1 flex-col">
				<div className="cursor-default">
					<div className="flex items-center gap-2">
						<NucleoFillIconPicker
							disabled
							buttonVariant="link"
							icon={icon}
							className="h-[1.1rem] min-h-[1rem] w-[1rem] min-w-[1.1rem] text-gray-700 opacity-100 dark:text-gray-300"
							buttonClassName="flex h-8 w-8 items-center justify-center rounded bg-gray-100 transition-colors dark:bg-gray-800"
							onChange={() => {
								/* read-only */
							}}
						/>
					</div>
					<Textarea
						name="review-title"
						value={title}
						readOnly={!canEdit}
						onChange={(event) => onTitleChange?.(event.target.value)}
						variant="borderless"
						noResize
						textSize="xxl"
						placeholder="Title"
						className="no-scrollbar mb-2 mt-4 w-full cursor-default bg-transparent"
						style={{ lineHeight: "1.2" }}
						fontWeight="bold"
						maxLength={200}
					/>
					{summary && (
						<Textarea
							name="review-summary"
							value={summary}
							readOnly
							variant="borderless"
							noResize
							textSize="md"
							placeholder="Summary"
							className="mb-7 flex w-full cursor-default flex-col bg-transparent text-gray-600 dark:text-gray-350"
							maxLength={200}
						/>
					)}
					{!isArticleCoverHidden(imageUrl) && (
						<div className="mt-7 overflow-hidden rounded-lg">
							{imageUrl ? (
								<img
									src={imageUrl}
									alt=""
									className="h-auto w-full object-cover"
								/>
							) : (
								<DefaultArticleCover
									icon={icon}
									logoUrl={workspace?.logoUrl}
									workspaceName={workspace?.name}
									accent={workspace?.accentColor}
								/>
							)}
						</div>
					)}
				</div>

				<DefaultEditor
					key={`review-${contentKey ?? ""}-${contentHtml.length}-${diffBaseHtml.length}-${discardedHunkIds?.size ?? 0}-${hunkRanges?.length ?? "plain"}`}
					extensions={[
						DiffRenderExtension.configure({
							baseHtml: diffBaseHtml,
							hunkRanges: hunkRanges ?? null,
							onProposalHover: onProposalHover ?? null,
							onDiscardHunk: onDiscardHunk ?? null,
						}),
					]}
					autofocus={false}
					initialContent={contentHtml}
					placeholder=""
					onUpdate={({ editor }) => {
						if (canEdit) onContentChange?.(editor.getHTML())
					}}
					editable={canEdit}
					className="mt-8 w-full max-w-[min(630px,100%)] flex-grow [&_.ProseMirror]:pb-20"
				/>
			</div>
		</div>
	)

	return (
		<div
			ref={scrollRef}
			className="scroll-container docs-scroller min-h-0 flex-1 cursor-default overflow-y-auto"
		>
			<ReviewDiff
				key={`diff-${contentKey ?? ""}`}
				canEditProposed={canEdit}
				onEditProposed={onProposedEdit}
				onEditProposedTitle={onTitleChange}
				layout={viewMode}
				icon={icon}
				articleTitle={title}
				baseHtml={diffBaseHtml}
				mergedHtml={contentHtml}
				hunkRanges={hunkRanges ?? null}
				titleChange={
					baseTitle !== undefined && baseTitle !== title
						? { from: baseTitle, to: title }
						: null
				}
				hoveredProposalId={hoveredProposalId ?? null}
				onHoverProposal={onProposalHover}
				onDiscardHunk={onDiscardHunk}
				foldUnchanged={foldUnchanged}
				fallback={legacyEditorView}
			/>
		</div>
	)
}

// A new article has nothing to diff against, so it opens in the live article's
// editor instead of a column of "added" rows.
function NewArticleEditor({
	proposal,
	icon,
	title,
	initialHtml,
	canEdit,
	onTitleChange,
	onContentChange,
}: {
	proposal: SuggestionProposal
	icon: string
	title: string
	/** Read once on mount; the editor owns the body after that. */
	initialHtml: string
	canEdit: boolean
	onTitleChange: (title: string) => void
	onContentChange: (html: string) => void
}) {
	const editorRef = useRef<{ getEditorInstance: () => Editor } | null>(null)
	const articleLinkPills = useArticleLinkPills()

	return (
		<div className="scroll-container docs-scroller min-h-0 flex-1 overflow-y-auto px-4 pt-8 sm:px-8">
			<div className="docs-table-room flex min-w-0">
				<div className="mx-auto flex min-w-0 max-w-[652px] flex-1 flex-col">
					<div className="flex min-w-0 items-center gap-2">
						<NucleoFillIconPicker
							disabled
							buttonVariant="link"
							icon={icon}
							className="h-[1.1rem] min-h-[1rem] w-[1rem] min-w-[1.1rem] text-gray-700 opacity-100 dark:text-gray-300"
							buttonClassName="flex h-8 w-8 items-center justify-center rounded bg-gray-100 dark:bg-gray-800"
							onChange={() => {
								/* the proposal sets the icon */
							}}
						/>
						<ProposalFolder proposal={proposal} className="text-sm" />
					</div>
					<Textarea
						name="review-new-title"
						value={title}
						readOnly={!canEdit}
						onChange={(event) => onTitleChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key !== "Enter") return
							event.preventDefault()
							editorRef.current?.getEditorInstance().commands.focus("start")
						}}
						variant="borderless"
						noResize
						textSize="xxl"
						placeholder="New article"
						className="no-scrollbar mb-2 mt-4 w-full bg-transparent"
						style={{ lineHeight: "1.2" }}
						fontWeight="bold"
						maxLength={200}
					/>
					<DefaultEditor
						ref={editorRef}
						extensions={[
							OrderedList.configure({
								HTMLAttributes: { class: "steps-list" },
							}),
							articleLinkPills,
							ShiftEnterParagraph,
						]}
						autofocus={false}
						initialContent={initialHtml}
						placeholder="Start writing and press '/' to add images, videos, or PDFs ..."
						onUpdate={({ editor }) => {
							if (canEdit) onContentChange(editor.getHTML())
						}}
						editable={canEdit}
						className="w-full max-w-[min(652px,100%)] flex-grow [&_.ProseMirror]:pb-72"
					/>
				</div>
			</div>
		</div>
	)
}

interface UserInfo {
	id: string
	name: string
	imageUrl: string | null
}

interface ReviewContentProps {
	proposals: SuggestionProposal[]
	outcomeById: Map<
		string,
		{ id: string; applied: boolean; conflicts: { description: string }[] }
	>
	articleId: string | null
	hoveredProposalId: string | null
	onHoverProposal: (id: string | null) => void
	onCardScroll?: (proposalId: string) => void
	discardedByProposal?: Map<string, string[]>
	hunkCountByProposal?: Map<string, number>
	onRestoreProposal?: (proposalId: string) => void
}

function useReviewRailUserData(proposals: SuggestionProposal[]) {
	const workspace = useZeroWorkspace()
	// Hint is only relevant when the rail surfaces AGENT proposals — those are
	// the ones the docs-proposal agent generates from issues + PRs, and GitHub
	// linkage is what unlocks PR-diff context. For pure manual edits there's
	// nothing for GitHub to inform.
	const hasAgentProposal = proposals.some((p) => p.authorType === "AGENT")
	const showGithubHint = hasAgentProposal && !workspace?.githubSettings
	const [teamMembers] = useQuery(queries.teamMembers())
	const userById = useMemo(() => {
		const map = new Map<string, UserInfo>()
		for (const m of teamMembers) {
			if (m.user) {
				map.set(m.user.id, {
					id: m.user.id,
					name: m.user.name ?? m.user.email ?? "Unknown",
					imageUrl: (m.user.imageUrl as string | null | undefined) ?? null,
				})
			}
		}
		return map
	}, [teamMembers])
	return { showGithubHint, userById }
}

function ReviewRailContent({
	proposals,
	outcomeById,
	articleId,
	hoveredProposalId,
	onHoverProposal,
	onCardScroll,
	discardedByProposal,
	hunkCountByProposal,
	onRestoreProposal,
}: ReviewContentProps) {
	const { showGithubHint, userById } = useReviewRailUserData(proposals)
	return (
		<>
			{showGithubHint && <GithubLinkHint />}
			{proposals.map((p) => (
				<ProposalCard
					key={p.id}
					proposal={p}
					outcome={
						outcomeById.get(p.id) ?? {
							id: p.id,
							applied: true,
							conflicts: [],
						}
					}
					articleId={articleId}
					isHovered={hoveredProposalId === p.id}
					onHoverProposal={onHoverProposal}
					onCardScroll={onCardScroll}
					author={
						p.createdByUserId ? (userById.get(p.createdByUserId) ?? null) : null
					}
					discardedHunkIds={discardedByProposal?.get(p.id) ?? []}
					hunkCount={hunkCountByProposal?.get(p.id) ?? 0}
					onRestore={
						onRestoreProposal ? () => onRestoreProposal(p.id) : undefined
					}
				/>
			))}
		</>
	)
}

function ReviewRail(props: ReviewContentProps) {
	return (
		<aside className="flex w-80 flex-shrink-0 flex-col overflow-hidden bg-white dark:bg-gray-900">
			<div className="no-scrollbar flex-1 space-y-3 overflow-y-auto px-4 pb-4 pt-9">
				<ReviewRailContent {...props} />
			</div>
		</aside>
	)
}

// Floating bottom-right pill on mobile that opens the proposals sheet. Surface
// is a real material — not a black/white inversion — so the control reads the
// same way in both themes: a calm white card in light, a softly-lifted dark
// card with a hairline top highlight in dark. A single neutral count chip
// (or the kind badge when there's only one proposal) replaces the noisy
// ring-stacked icon row the earlier version used.
function MobileProposalsPill({
	proposals,
	hidden,
	onClick,
}: {
	proposals: SuggestionProposal[]
	hidden: boolean
	onClick: () => void
}) {
	const count = proposals.length
	const single = count === 1 ? proposals[0] : null
	const singleHeadline = single ? deriveHeadline(single) : null
	const truncatedHeadline =
		singleHeadline && singleHeadline.length > 22
			? `${singleHeadline.slice(0, 21).trimEnd()}…`
			: singleHeadline
	const SingleIcon = single ? iconForProposal(single) : null

	return (
		<AnimatePresence>
			{!hidden && (
				<motion.button
					type="button"
					onClick={onClick}
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: 8 }}
					transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
					whileTap={{ scale: 0.97 }}
					aria-label={
						single
							? `Open suggestion: ${singleHeadline}`
							: `Open ${count} suggestions`
					}
					className={cn(
						"group fixed z-40",
						"bottom-[max(env(safe-area-inset-bottom),16px)] right-4",
						"inline-flex h-11 max-w-[calc(100vw-32px)] items-center gap-2 rounded-full border pl-1.5 pr-3.5 backdrop-blur-xl backdrop-saturate-150",
						"border-gray-200/40 bg-transparent text-gray-900",
						"shadow-[0_1px_2px_rgb(0_0_0/0.04),0_10px_30px_-10px_rgb(0_0_0/0.15)]",
						"dark:border-gray-750/40 dark:text-gray-100",
						"dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04),0_14px_36px_-12px_rgb(0_0_0/0.5)]",
						"ease-[cubic-bezier(.23,1,.32,1)] transition-[transform,box-shadow] duration-200",
					)}
				>
					{single && SingleIcon ? (
						<span
							className={cn(
								"flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full",
								kindBadgeClass(single.kind),
							)}
						>
							<SingleIcon className="h-3.5 w-3.5" strokeWidth={2} />
						</span>
					) : (
						<span
							className={cn(
								"flex h-8 min-w-8 flex-shrink-0 items-center justify-center rounded-full px-2",
								"bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900",
								"text-xs font-semibold tabular-nums leading-none",
							)}
						>
							{count}
						</span>
					)}
					<span className="min-w-0 truncate text-sm font-medium">
						{truncatedHeadline ?? `Suggestion${count === 1 ? "" : "s"}`}
					</span>
					<ChevronUp
						aria-hidden
						strokeWidth={2.25}
						className={cn(
							"h-3 w-3 flex-shrink-0 text-gray-500 dark:text-gray-400",
							"ease-[cubic-bezier(.23,1,.32,1)] transition-transform duration-200",
							"group-hover:-translate-y-0.5",
						)}
					/>
				</motion.button>
			)}
		</AnimatePresence>
	)
}

function MobileProposalsSheet({
	open,
	onOpenChange,
	proposals,
	outcomeById,
	articleId,
	hoveredProposalId,
	onHoverProposal,
	onCardScroll,
}: ReviewContentProps & {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="bottom"
				isOverlayVisible
				className={cn(
					"!gap-0 rounded-t-2xl border-t-0 !p-0",
					"flex max-h-[85dvh] flex-col",
					// Hide the default close X — drag-handle + tap-overlay are the
					// affordances on mobile, and a corner X clutters a bottom sheet.
					"[&>button:last-child]:hidden",
				)}
			>
				<div className="flex justify-center pb-1 pt-2.5">
					<span
						aria-hidden
						className="block h-1 w-9 rounded-full bg-gray-300 dark:bg-gray-700"
					/>
				</div>
				<div className="flex h-12 flex-shrink-0 items-center px-4">
					<span className="cursor-default text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400">
						Explanation
					</span>
				</div>
				<div className="no-scrollbar flex-1 space-y-3 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+80px)] pt-1">
					<ReviewRailContent
						proposals={proposals}
						outcomeById={outcomeById}
						articleId={articleId}
						hoveredProposalId={hoveredProposalId}
						onHoverProposal={onHoverProposal}
						onCardScroll={onCardScroll}
					/>
				</div>
			</SheetContent>
		</Sheet>
	)
}

// Hint shown above the proposal cards when the workspace has agent proposals
// but no GitHub link. The agent reads PR diffs when scoping doc updates;
// without GitHub it only has the issue text. Renders as a quiet card with a
// soft-elevation hover, matching the proposal-card visual language so the
// rail feels consistent.
function GithubLinkHint() {
	return (
		<Link
			to="/settings/integrations/github"
			className={cn(
				"group block rounded-xl border p-3 no-underline",
				"border-gray-300 bg-gray-50 dark:border-gray-750 dark:bg-gray-850",
				"ease-[cubic-bezier(.23,1,.32,1)] transition-[background-color,box-shadow] duration-200",
				"hover:bg-white hover:shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_8px_24px_-6px_rgb(0_0_0/0.10)]",
				"dark:hover:bg-gray-800 dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.07),0_8px_24px_-6px_rgb(0_0_0/0.5)]",
			)}
		>
			<div className="flex min-w-0 items-start gap-2.5">
				<span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-gray-600 dark:text-gray-300">
					<Github aria-hidden strokeWidth={1.75} className="h-3 w-3" />
				</span>
				<div className="min-w-0 flex-1">
					<p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
						Link a GitHub repository for sharper suggestions. The agent reads
						recent PR diffs to scope each update.
					</p>
					<p className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-gray-900 dark:text-gray-100">
						Connect repository
						<ArrowRight
							aria-hidden
							className="ease-[cubic-bezier(.23,1,.32,1)] h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5"
						/>
					</p>
				</div>
			</div>
		</Link>
	)
}

// Module-scoped tracker so a new proposal-action toast replaces any prior
// proposal-action toast still visible. Without this, rapid Accept/Skip clicks
// stack 10s undo toasts on top of each other. We dismiss the prior toast by id
// directly, since relying solely on `removePendingMessage` can miss a re-render
// when the UndoContext value memo's `[length]` dep happens to be unchanged
// across remove+add in the same tick.
let lastProposalAction: { toastId: string; messageId: string } | null = null

function ProposalCard({
	proposal,
	outcome,
	articleId,
	isHovered,
	onHoverProposal,
	onCardScroll,
	author,
	discardedHunkIds = [],
	hunkCount = 0,
	onRestore,
}: {
	proposal: SuggestionProposal
	outcome: {
		id: string
		applied: boolean
		conflicts: { description: string }[]
	}
	articleId: string | null
	isHovered: boolean
	onHoverProposal: (id: string | null) => void
	onCardScroll?: (proposalId: string) => void
	author: UserInfo | null
	discardedHunkIds?: string[]
	hunkCount?: number
	onRestore?: () => void
}) {
	const zero = useZero()
	const navigate = useNavigate()
	const router = useRouter()
	const workspace = useZeroWorkspace()
	const decodedToken = useAuthData()
	const [articles] = useQuery(queries.helpCenterArticles())
	const { rows, visibleRows } = useArticleSuggestions()
	const { setPendingMessage, removePendingMessage } = useUndoContext()
	const [pending, setPending] = useState<"accept" | "skip" | null>(null)
	const busy = pending !== null
	const draftKey = articleId ?? createDraftKey(proposal.id)
	const draft = useReviewDraftStore((state) => state.drafts[draftKey])
	const clearDraft = useReviewDraftStore((state) => state.clearDraft)
	const hasConflict = outcome.conflicts.length > 0
	const allDiscarded = hunkCount > 0 && discardedHunkIds.length >= hunkCount

	const dismissPriorActionToast = () => {
		if (lastProposalAction) {
			toast.dismiss(lastProposalAction.toastId)
			removePendingMessage(lastProposalAction.messageId)
		}
	}

	const onAccept = async () => {
		if (busy) return
		setPending("accept")
		const newArticleVersionId = generateId()
		const newArticleId = proposal.kind === "CREATE" ? generateId() : undefined
		const newArticleUrlName =
			proposal.kind === "CREATE" ? generateId() : undefined
		try {
			await mutateOk(
				zero.mutate(
					mutators.helpCenterArticleProposals.accept({
						id: proposal.id,
						newArticleVersionId,
						newArticleId,
						newArticleUrlName,
						discardedHunkIds: discardedHunkIds.length
							? discardedHunkIds
							: undefined,
						editedHtml: draft?.html,
						editedTitle: draft?.title,
					}),
				),
			)
			clearDraft(draftKey)
			const currentKey = articleId ?? createDraftKey(proposal.id)
			// Only jump when this empties the row; otherwise stay so the article's
			// remaining proposals stay visible.
			const remainingOnRow = (
				rows.find((r) => r.key === currentKey)?.proposals ?? []
			).filter((p) => p.id !== proposal.id).length
			const shouldNavigateToNext = remainingOnRow === 0
			// The docs tree only holds foldered articles, so a folderless one stays here.
			const openedArticleId = proposal.proposedGroupId
				? newArticleId
				: undefined
			dismissPriorActionToast()
			setPendingMessage(proposal.id)
			const acceptToastId = generateId()
			const linkedArticleId =
				proposal.kind === "CREATE" ? newArticleId : (articleId ?? undefined)
			const existingArticle = articles?.find((a) => a.id === linkedArticleId)
			const linkedTitle =
				proposal.kind === "CREATE"
					? draft?.title?.trim() || proposal.proposedTitle || "Untitled"
					: existingArticle?.title
			const linkedIcon =
				proposal.kind === "CREATE" ? null : (existingArticle?.icon ?? null)
			const toastId = toast.undo({
				id: acceptToastId,
				title: "Suggestion accepted",
				icon: iconForKind(proposal.kind),
				description:
					!openedArticleId &&
					proposal.kind !== "DELETE" &&
					linkedArticleId &&
					linkedTitle ? (
						<AcceptedArticleLink
							title={linkedTitle}
							icon={linkedIcon}
							onOpen={() => {
								toast.dismiss(acceptToastId)
								void navigate({
									to: "/edit-docs/$id",
									params: { id: linkedArticleId },
								})
							}}
						/>
					) : undefined,
				data: { messageId: proposal.id },
				layout: "inline",
				action: {
					label: "Undo",
					callback: () => {
						removePendingMessage(proposal.id)
						if (lastProposalAction?.messageId === proposal.id) {
							lastProposalAction = null
						}
						void zero
							.mutate(
								mutators.helpCenterArticleProposals.unaccept({
									id: proposal.id,
									newArticleVersionId,
									newArticleId,
								}),
							)
							.client.catch((err: unknown) => {
								console.error(err)
								captureError(err, {
									domain: "general",
									action: "restore docs draft",
									workspaceId: workspace?.id,
									userId: decodedToken.userId,
									extra: { proposalId: proposal.id },
								})
								toast.error({ title: "Failed to undo" })
							})
						if (shouldNavigateToNext) {
							router.history.back()
						}
					},
					shortcut: <Shortcut>Z</Shortcut>,
				},
			})
			lastProposalAction = { toastId, messageId: proposal.id }
			if (openedArticleId) {
				void navigate({ to: "/edit-docs/$id", params: { id: openedArticleId } })
			} else if (shouldNavigateToNext) {
				const next = nextSuggestionRow(visibleRows, currentKey)
				void navigate({ href: next ? reviewRouteForRow(next) : "/edit-docs" })
			}
		} catch (err) {
			console.error(err)
			captureError(err, {
				domain: "general",
				action: "accept docs draft",
				workspaceId: workspace?.id,
				userId: decodedToken.userId,
				extra: { proposalId: proposal.id },
			})
			toast.error({ title: "Failed to accept suggestion" })
		} finally {
			setPending(null)
		}
	}

	const onSkip = async () => {
		if (busy) return
		setPending("skip")
		try {
			await mutateOk(
				zero.mutate(
					mutators.helpCenterArticleProposals.reject({
						id: proposal.id,
					}),
				),
			)
			const currentKey = articleId ?? createDraftKey(proposal.id)
			const remainingOnRow = (
				rows.find((r) => r.key === currentKey)?.proposals ?? []
			).filter((p) => p.id !== proposal.id).length
			const shouldNavigateToNext = remainingOnRow === 0
			dismissPriorActionToast()
			setPendingMessage(proposal.id)
			const toastId = toast.undo({
				title: "Suggestion skipped",
				icon: iconForKind(proposal.kind),
				data: { messageId: proposal.id },
				layout: "inline",
				action: {
					label: "Undo",
					callback: () => {
						removePendingMessage(proposal.id)
						if (lastProposalAction?.messageId === proposal.id) {
							lastProposalAction = null
						}
						void zero
							.mutate(
								mutators.helpCenterArticleProposals.reopen({ id: proposal.id }),
							)
							.client.catch((err: unknown) => {
								console.error(err)
								captureError(err, {
									domain: "general",
									action: "restore docs draft",
									workspaceId: workspace?.id,
									userId: decodedToken.userId,
									extra: { proposalId: proposal.id },
								})
								toast.error({ title: "Failed to undo" })
							})
						if (shouldNavigateToNext) {
							router.history.back()
						}
					},
					shortcut: <Shortcut>Z</Shortcut>,
				},
			})
			lastProposalAction = { toastId, messageId: proposal.id }
			if (shouldNavigateToNext) {
				const next = nextSuggestionRow(visibleRows, currentKey)
				void navigate({ href: next ? reviewRouteForRow(next) : "/edit-docs" })
			}
		} catch (err) {
			console.error(err)
			captureError(err, {
				domain: "general",
				action: "decline docs draft",
				workspaceId: workspace?.id,
				userId: decodedToken.userId,
				extra: { proposalId: proposal.id },
			})
			toast.error({ title: "Failed to skip suggestion" })
		} finally {
			setPending(null)
		}
	}

	const isAgent = proposal.authorType === "AGENT"
	const isNewArticle = proposal.kind === "CREATE"
	const headline = deriveHeadline(proposal)
	const HeadlineIcon = iconForProposal(proposal)
	const relativeSuggestedAt = formatDistanceToNow(proposal.createdAt, {
		addSuffix: true,
	})
	const suggestedAt =
		relativeSuggestedAt.charAt(0).toUpperCase() + relativeSuggestedAt.slice(1)

	const scrollEditorToProposal = () => {
		if (onCardScroll) {
			onCardScroll(proposal.id)
			return
		}
		const editorDiffs = document.querySelectorAll<HTMLElement>(
			"[data-proposal-id][data-proposal-diff]",
		)
		for (const el of editorDiffs) {
			if (el.getAttribute("data-proposal-id") === proposal.id) {
				const container = scrollParent(el)
				if (container) scrollWithinContainer(container, el)
				return
			}
		}
	}

	return (
		<div
			data-proposal-id={proposal.id}
			data-proposal-card=""
			role="button"
			tabIndex={0}
			onMouseEnter={() => onHoverProposal(proposal.id)}
			onMouseLeave={() => onHoverProposal(null)}
			onClick={(event) => {
				// Don't hijack inner-button / inner-link clicks (Skip / Accept,
				// source chips). Anything else on the card scrolls the editor
				// to the first occurrence of this proposal's diff.
				const target = event.target as Element | null
				if (target?.closest("button, a")) return
				// Mobile sheet hands us a coordinator that closes the sheet first
				// and then scrolls the diff into view, so the user sees the
				// editor come into focus on the right diff. Desktop uses the
				// in-place sync scroll.
				scrollEditorToProposal()
			}}
			onKeyDown={(event) => {
				if (event.key !== "Enter" && event.key !== " ") return
				const target = event.target as Element | null
				if (target?.closest("button, a")) return
				event.preventDefault()
				scrollEditorToProposal()
			}}
			data-proposal-kind={proposal.kind}
			className={cn(
				"cursor-default rounded-xl border p-4",
				isNewArticle
					? "border-green-500/60 bg-green-500/[0.04] dark:border-green-500/50 dark:bg-green-500/[0.06]"
					: "border-gray-300 bg-gray-50 dark:border-gray-750 dark:bg-gray-850",
				"ease-[cubic-bezier(.23,1,.32,1)] transition-[background-color,box-shadow] duration-200",
				// The card is a review surface; its buttons set their own cursor.
				"[&_a]:cursor-default",
				isHovered && [
					"bg-white dark:bg-gray-800",
					// Hairline ring + a wider, deeper drop. Together they read
					// as "this card is the focused one" without lifting the
					// whole card off the page.
					"shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_8px_24px_-6px_rgb(0_0_0/0.12)]",
					"dark:shadow-[0_0_0_1px_rgb(255_255_255/0.07),0_8px_28px_-6px_rgb(0_0_0/0.6)]",
				],
			)}
		>
			{/* Headline: kind-tinted icon badge + auto-generated title, with the
			    suggestion time aligned under the title. */}
			<div className="mb-3 flex min-w-0 items-start gap-2.5">
				<span
					className={cn(
						"mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md",
						kindBadgeClass(proposal.kind),
					)}
				>
					<HeadlineIcon className="h-3 w-3" />
				</span>
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<h3
						title={headline}
						className="min-w-0 truncate text-sm font-medium leading-snug text-gray-900 dark:text-gray-100"
					>
						{headline}
					</h3>
					<span className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
						{isNewArticle && isAgent && (
							<Badge variant="green" size="sm" className="flex-none">
								New article
							</Badge>
						)}
						<span className="truncate">
							{isApiProposal(proposal)
								? `${suggestedAt} · Made via API`
								: suggestedAt}
						</span>
					</span>
				</div>
			</div>

			{isNewArticle && (
				<ProposalFolder proposal={proposal} className="mb-3 text-xs" />
			)}

			{/* Agent-written rationale — 1-3 sentence explanation of the
			    change, set via the `set_description` tool. Null for standalone
			    USER proposals so the card stays compact. */}
			{proposal.description && (
				<p className="mb-3 whitespace-pre-wrap text-xs leading-relaxed text-gray-600 dark:text-gray-400">
					{proposal.description}
				</p>
			)}

			{proposal.agentReview && (
				<p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
					{proposal.agentReview}
				</p>
			)}

			<ProposalSources sources={proposal.sources} />

			{/* The icon already marks agent authors, so only humans get a name. */}
			{!isAgent && author && (
				<div className="mb-3 flex items-center gap-1.5">
					<Avatar size="xs" name={author.name} image={author.imageUrl} />
					<span className="text-xs text-gray-500 dark:text-gray-400">
						{author.name}
					</span>
				</div>
			)}

			{hasConflict && (
				<div className="mb-4 rounded-md bg-amber-50 px-2.5 py-2 text-xs leading-relaxed text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
					{outcome.conflicts.length === 1
						? "1 change couldn't be merged. Accepting will apply the rest."
						: `${outcome.conflicts.length} changes couldn't be merged. Accepting will apply the rest.`}
				</div>
			)}

			{discardedHunkIds.length > 0 && (
				<div className="mb-2.5 flex items-center justify-between">
					<span className="text-[11px] font-medium tabular-nums text-gray-400 dark:text-gray-500">
						{discardedHunkIds.length} skipped
					</span>
					{onRestore && (
						<button
							type="button"
							onClick={onRestore}
							className="text-[11px] font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
						>
							Restore
						</button>
					)}
				</div>
			)}

			<div className="flex items-center gap-2">
				<Button
					variant="outline"
					onClick={onSkip}
					isLoading={pending === "skip"}
					disabled={busy}
					className="flex-1"
				>
					Skip
				</Button>
				<Button
					onClick={onAccept}
					isLoading={pending === "accept"}
					disabled={busy || allDiscarded}
					className="flex-1 disabled:opacity-40"
				>
					{discardedHunkIds.length > 0 ? "Accept rest" : "Accept"}
				</Button>
			</div>
		</div>
	)
}

// Renders the accepted article as a clickable row (icon + title) in the
// "Suggestion accepted" toast. Reads live title/icon from the shared articles
// subscription so it reflects a rename applied by the same proposal.
function AcceptedArticleLink({
	title,
	icon,
	onOpen,
}: {
	title: string
	icon: string | null
	onOpen: () => void
}) {
	return (
		<button
			type="button"
			onClick={onOpen}
			className="-mx-1 flex max-w-full items-center gap-1.5 rounded-md px-1 py-0.5 text-left hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800 dark:hover:text-gray-100"
		>
			<LucideIcon icon={icon} className="h-3.5 w-3.5 shrink-0" />
			<span className="truncate">{title}</span>
		</button>
	)
}

function kindBadgeClass(kind: "CREATE" | "EDIT" | "DELETE"): string {
	if (kind === "DELETE") {
		return "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400"
	}
	if (kind === "CREATE") {
		return "bg-green-500/10 text-green-500 dark:bg-green-500/15"
	}
	return "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400"
}

// One canonical headline per proposal. AGENT proposals prefer the group's
// 3-word headline (set by the docs-proposal agent); USER proposals prefer the
// proposed title. Falls through to a kind-specific default.
function deriveHeadline(p: SuggestionProposal): string {
	if (p.authorType === "AGENT" && p.proposalGroupTitle) {
		return p.proposalGroupTitle
	}
	if (p.kind === "CREATE" && p.proposedTitle) {
		return `New article: ${p.proposedTitle}`
	}
	if (p.authorType === "USER" && p.proposedTitle && p.kind === "EDIT") {
		return `Rename to "${p.proposedTitle}"`
	}
	if (p.kind === "DELETE") return "Removal proposal"
	return p.authorType === "AGENT" ? "Agent suggestion" : "Suggested change"
}
